/**
 * The Scans section's own vocabulary, mapped onto the panel-wide design tokens
 * in component/common/designSystem/theme.js — the same split
 * selfDevelopmentTheme.js and homeTestingTheme.js use: only domain meaning
 * lives here, palettes belong to the design system.
 */
import { CHART_COLORS } from '../../../../component/common/designSystem';

/** The three scans, in the order the section presents them everywhere. */
export const SCAN_ORDER = ['skin', 'teeth', 'age'];

export const SCAN_LABELS = {
  skin: 'Skin Scan',
  teeth: 'Teeth Scan',
  age: 'Age Scan'
};

/**
 * One hue per scan, fixed. A scan keeps its colour on the overview donut, in
 * the scan-by-scan table and on its own page, so somebody scanning the section
 * learns the mapping once. No hue carries a verdict — three scans are three
 * different things to photograph, not a scale.
 */
export const SCAN_COLORS = {
  skin: CHART_COLORS.orange,
  teeth: CHART_COLORS.teal,
  age: CHART_COLORS.brand
};

export const scanColor = (key) => SCAN_COLORS[key] || CHART_COLORS.gray;

/** Donut colours in SCAN_ORDER, for any chart whose slices are the three scans. */
export const SCAN_RAMP = SCAN_ORDER.map((key) => SCAN_COLORS[key]);

/**
 * Analysed / Failed / Still processing, in that fixed order. This is the one
 * ramp in the section that IS a verdict — but on the software, not on anybody's
 * health. Red here means the scan broke.
 */
export const STATUS_RAMP = [CHART_COLORS.green, CHART_COLORS.red, CHART_COLORS.gray];

/** How many scans one person has taken — sparse to frequent. */
export const VOLUME_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.blue,
  CHART_COLORS.moss
];

/**
 * How many of the three scans one person has tried. Climbs to green: breadth
 * is the one distribution in the section that genuinely reads as better.
 */
export const BREADTH_RAMP = [CHART_COLORS.brandSoft, CHART_COLORS.blue, CHART_COLORS.green];

/**
 * Time from upload to a finished analysis. Fast is better here, so it runs
 * green → red — this is a latency reading on the AI pipeline, nothing else.
 */
export const SPEED_RAMP = [
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.blue,
  CHART_COLORS.amber,
  CHART_COLORS.red
];

/**
 * Average gap between one person's scans. Deliberately neutral: there is no
 * correct interval for photographing your own teeth, and colouring "3 months+"
 * red would invent a target nobody set.
 */
export const CADENCE_RAMP = [
  CHART_COLORS.teal,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.moss
];

/**
 * How complete a submission was — how many photos, how many questions
 * answered, how many readings came back. None → all, so it climbs to green:
 * a complete submission is the one that gives the person a usable result.
 */
export const COMPLETENESS_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.green
];

/** The steps of one scan: taken → analysed → report ready. */
export const FUNNEL_RAMP = [CHART_COLORS.brandSoft, CHART_COLORS.blue, CHART_COLORS.green];

/**
 * Self-reported dental answers, best-first on their own scale. Amber and red
 * here describe an answer somebody gave about their own habits — "more than two
 * years since a dentist" — not a finding about their mouth.
 */
export const HABIT_RAMP = [
  CHART_COLORS.green,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.red
];

/** A scan's status chip on the per-person history list. */
export const STATUS_TONES = {
  done: 'success',
  failed: 'danger',
  processing: 'neutral',
  pending: 'neutral'
};

export const STATUS_TEXT = {
  done: 'Analysed',
  failed: 'Failed',
  processing: 'Processing',
  pending: 'Waiting'
};
